<template>
  <DModal :show-close="false" :isBgOverlay="true" :isCloseOnBgClick="false" @close="$emit('close')" :appendToBody="true"
          style="z-index: 12312312312312" :placeInTop="true">
    <div class="d-modal text-dark" slot="insertComponent">
      <div class="d-modal__header">
        <div>
          <h2>{{ employmentIds.length === 1 ? 'Opret engangsydelse' : 'Opret engangsydelser' }}</h2>
          <p class="d-modal__subtitle">{{ subtitleText }}</p>
        </div>
        <div class="font-large icon__bg-circle d-modal__header__close" @click="$emit('close')">
          <span class="icon-cross2"></span>
        </div>
      </div>

      <div class="d-modal__content">
        <div class="create-form__grid">
          <div class="d-input-group cf-loenart">
            <label class="d-label">Lønart *</label>
            <DSelect
              ref="loenartSelect"
              :items="salaryCodesForDSelect"
              :multiple="false"
              :placeholder="'Vælg lønart'"
              :itemsSelected="selectedSalaryCodeForDSelect"
              :isShowRemoveButton="false"
              @add="onSelectLoenart"
              @remove="onSelectLoenart(null)"
            />
          </div>
          <div class="d-input-group cf-antal">
            <label class="d-label">Antal</label>
            <input v-model="antal" type="text" inputmode="decimal" class="d-input width-100"
                   :disabled="satsHasValue"
                   placeholder="Indtast antal"/>
          </div>
          <div class="d-input-group cf-beloeb">
            <label class="d-label">Beløb</label>
            <input v-model="sats" type="text" inputmode="decimal" class="d-input width-100"
                   :disabled="antalHasValue"
                   placeholder="Indtast beløb"/>
          </div>
          <p class="cf-hint text-explainer">
            Angiv enten Antal (taksten for lønarten anvendes) eller Beløb. De to felter kan ikke udfyldes samtidig.
          </p>
          <div class="d-input-group cf-dato">
            <label class="d-label">Aktivitetsdato *</label>
            <DateSelector class="width-100" :date="dato" :width100="true" @changed="setDato($event)"/>
          </div>
          <div class="d-input-group cf-omk">
            <label class="d-label">Afvigende omkostningssted</label>
            <DSelect
              :items="costCentersForDSelect"
              :multiple="false"
              :placeholder="'Vælg afvigende omkostningssted (valgfri)'"
              :itemsSelected="selectedCostCenterForDSelect"
              :isShowRemoveButton="true"
              @add="onSelectCostCenter"
              @remove="onSelectCostCenter(null)"
            />
          </div>
          <div class="d-input-group cf-beskrivelse">
            <label class="d-label">Beskrivelse</label>
            <input v-model="description" type="text" class="d-input width-100"
                   placeholder="Indtast beskrivelse (valgfri)"/>
          </div>
        </div>
        <p v-if="validationMessage" class="text-explainer text-explainer--danger">{{ validationMessage }}</p>
      </div>

      <div class="d-modal__buttons">
        <div class="d-modal__buttons__child">
          <button class="do-bttn" @click="$emit('close')">{{ $t('vue2.common.cancel') }}</button>
          <button class="do-bttn do-bttn--submit"
                  :class="{'do-bttn--submit--loading': isSubmitting, 'do-bttn--submit--disabled': !isValid}"
                  :disabled="!isValid || isSubmitting"
                  @click="submit"
                  :content="saveKeyTooltip"
                  v-tippy="{
                    arrow: true,
                      placement : 'right',
                      animateFill: false,
                      animation : 'shift-away',
                      boundary: 'window',
                      zIndex: 12312312312312
                    }">
            {{ submitButtonLabel }}
          </button>
        </div>
      </div>
    </div>
  </DModal>
</template>

<script>
import DModal from '../../common/DModal.vue'
import DateSelector from '../../datepicker/DateSelector.vue'
import moment from 'moment'
import eventbus from '../../../eventbus'
import docendo from '../../../docendo'
import DSelect from '../../DSelect/DSelect.vue'

export default {
  components: {DSelect, DModal, DateSelector},
  props: {
    schoolId: {
      type: Number,
      required: true
    },
    employmentIds: {
      type: Array,
      default: () => []
    },
    subtitle: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      dato: moment(),
      salaryCodeId: null,
      salaryCostCenterId: null,
      antal: '',
      sats: '',
      description: '',
      isSubmitting: false,
      validationMessage: ''
    }
  },
  computed: {
    metaKey () {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
      return isMac ? '⌘' : 'CTRL'
    },
    saveKeyTooltip () {
      return '<p class="tooltip-shortcut tooltip-shortcut--double-key"><span>' + this.metaKey + '</span>' + '<span>Enter</span></p>'
    },
    subtitleText () {
      if (this.subtitle) return this.subtitle
      return 'På ' + this.employmentIds.length + ' ' + (this.employmentIds.length === 1 ? 'ansættelse' : 'ansættelser')
    },
    submitButtonLabel () {
      if (this.employmentIds.length <= 1) {
        return 'Opret engangsydelse'
      }
      return 'Opret ' + this.employmentIds.length + ' engangsydelser'
    },
    antalHasValue () {
      return this.antal !== '' && this.antal != null
    },
    satsHasValue () {
      return this.sats !== '' && this.sats != null
    },
    isValid () {
      const hasEmployments = this.employmentIds.length > 0
      const hasDato = !!this.dato && moment(this.dato).isValid()
      const hasLoenart = this.salaryCodeId != null
      // SD-model: enten enheder eller sats skal udfyldes – men ikke begge.
      const exactlyOneAmount = (this.antalHasValue || this.satsHasValue) && !(this.antalHasValue && this.satsHasValue)
      return hasEmployments && hasDato && hasLoenart && exactlyOneAmount && this.antalValid && this.satsValid
    },
    antalValid () {
      if (!this.antalHasValue) return true
      const antal = parseFloat(String(this.antal).replace(',', '.'))
      return !isNaN(antal) && antal >= -999.99 && antal <= 999.99
    },
    satsValid () {
      if (!this.satsHasValue) return true
      const sats = parseFloat(String(this.sats).replace(',', '.'))
      return !isNaN(sats) && Math.abs(sats) <= 99999.99
    },
    salaryCodes () {
      return this.$store.getters.salaryCodesSorted || []
    },
    salaryCodesForDSelect () {
      if (!this.salaryCodes || !Array.isArray(this.salaryCodes)) {
        return []
      }
      return this.salaryCodes
        .filter(sc => sc.loenart)
        .map(sc => {
          return {
            id: sc.id,
            name: sc.vSupplementTitle,
            value: sc.loenart,
            original: sc
          }
        })
    },
    selectedSalaryCodeForDSelect () {
      if (this.salaryCodeId == null) {
        return []
      }
      const found = this.salaryCodesForDSelect.find(item => item.id === this.salaryCodeId)
      return found ? [found] : []
    },
    costCenters () {
      return this.$store.getters.costCenters || []
    },
    costCentersForDSelect () {
      return this.costCenters.map(cc => {
        const code = cc.code != null ? String(cc.code) : ''
        const title = cc.title || cc.name || ''
        return {
          id: cc.id,
          name: code + (title ? ' - ' + title : ''),
          value: code,
          original: cc
        }
      })
    },
    selectedCostCenterForDSelect () {
      if (this.salaryCostCenterId == null) {
        return []
      }
      const found = this.costCentersForDSelect.find(item => item.id === this.salaryCostCenterId)
      return found ? [found] : []
    },
    employmentsById () {
      const map = {}
      const list = (this.$store.state.employments && this.$store.state.employments.employments) || []
      for (let i = 0; i < list.length; i++) {
        map[list[i].id] = list[i]
      }
      return map
    },
    // Bulk-endpointet sætter samme user_id på alle poster, så vi grupperer de valgte
    // ansættelser pr. ansat og laver ét kald pr. ansat for at få korrekt user_id.
    employmentsGroupedByUser () {
      const groups = {}
      const byId = this.employmentsById
      for (let i = 0; i < this.employmentIds.length; i++) {
        const id = this.employmentIds[i]
        const emp = byId[id]
        const userId = emp ? emp.user_id : null
        const key = userId != null ? String(userId) : 'unknown'
        if (!groups[key]) groups[key] = {userId: userId, employmentIds: []}
        groups[key].employmentIds.push(id)
      }
      return Object.keys(groups).map(k => groups[k])
    }
  },
  methods: {
    onSelectLoenart (item) {
      this.salaryCodeId = item ? item.id : null
    },
    onSelectCostCenter (item) {
      this.salaryCostCenterId = item ? item.id : null
    },
    setDato (date) {
      this.dato = date
    },
    onGlobalKeydown (evt) {
      const isSubmitCombo = (evt.metaKey || evt.ctrlKey) && (evt.key === 'Enter' || evt.keyCode === 13)
      if (!isSubmitCombo) return
      if (this.isSubmitting || !this.isValid) return
      evt.preventDefault()
      this.submit()
    },
    submit () {
      this.validationMessage = ''
      if (!this.isValid) {
        this.validationMessage = 'Udfyld lønart, aktivitetsdato og enten antal eller beløb'
        return
      }
      if (!this.schoolId) {
        this.validationMessage = 'Mangler schoolId'
        return
      }

      const date = moment(this.dato)
      const baseEngangsydelse = {
        salary_code_id: this.salaryCodeId,
        aar: parseInt(date.format('YY'), 10),
        maaned: date.month() + 1,
        dag: date.date()
      }
      if (this.antalHasValue) {
        baseEngangsydelse.enheder = parseFloat(String(this.antal).replace(',', '.'))
      }
      if (this.satsHasValue) {
        baseEngangsydelse.sats = parseFloat(String(this.sats).replace(',', '.'))
      }
      if (this.salaryCostCenterId != null) {
        baseEngangsydelse.salary_cost_center_id = this.salaryCostCenterId
      }
      if (this.description.trim() !== '') {
        baseEngangsydelse.description = this.description.trim()
      }

      const groups = this.employmentsGroupedByUser
      this.isSubmitting = true
      let pending = groups.length
      let createdCount = 0
      let hadError = false

      const finish = () => {
        if (pending > 0) return
        this.isSubmitting = false
        if (hadError) {
          this.validationMessage = 'Kunne ikke oprette alle engangsydelser'
          eventbus.toast = {text: 'Kunne ikke oprette alle engangsydelser', success: false, position: 'bottomLeft'}
          return
        }
        eventbus.toast = {
          text: (createdCount === 1 ? 'Engangsydelse' : createdCount + ' engangsydelser') + ' lagt i udbakken. De sendes automatisk til SD.',
          success: true,
          position: 'bottomLeft'
        }
        this.$emit('created')
        this.$emit('close')
      }

      groups.forEach(group => {
        const engangsydelse = Object.assign({}, baseEngangsydelse)
        if (group.userId != null) engangsydelse.user_id = group.userId
        docendo.bulkCreateSilkeborgDataEngangsydelser(
          this.schoolId,
          group.employmentIds,
          engangsydelse,
          'ydelse',
          success => {
            pending--
            if (success && success.status === 'error') {
              hadError = true
            } else {
              createdCount += Array.isArray(success) ? success.length : group.employmentIds.length
            }
            finish()
          },
          failure => {
            console.log('failure', failure)
            pending--
            hadError = true
            finish()
          }
        )
      })
    }
  },
  mounted () {
    if (!this.salaryCodes.length) this.$store.dispatch('readSalaryCodes')
    if (!this.costCenters.length) this.$store.dispatch('readCostCenters')
    window.addEventListener('keydown', this.onGlobalKeydown)
    this.didAddNoScroll = !document.body.classList.contains('no-scroll')
    if (this.didAddNoScroll) {
      document.body.classList.add('no-scroll')
    }
    this.$nextTick(() => {
      setTimeout(() => {
        if (this.$refs.loenartSelect) this.$refs.loenartSelect.setFocus()
      }, 300)
    })
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.onGlobalKeydown)
    if (this.didAddNoScroll) {
      document.body.classList.remove('no-scroll')
    }
  }
}
</script>

<style scoped>
@import "../../../assets/css/variables.css";

.d-modal {
  width: 600px;
  max-width: calc(100vw - 32px);
  border-radius: 16px;
  position: relative;
}

.d-modal__header {
  margin-bottom: 0;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.d-modal__subtitle {
  margin: 0.25rem 0 0;
  font-size: 13px;
  color: #6b7280;
}

.d-modal__buttons {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1rem 2rem;
  border-top: 1px solid #eee;
  margin: 0;
}

.d-modal__buttons__child {
  display: flex;
  gap: 0.5rem;
}

.d-modal__content {
  padding: 1rem;
  overscroll-behavior: contain;
  max-height: calc(100vh - 52px - 80px);
  overflow-y: auto;
}

.create-form__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
}

.cf-loenart {
  grid-column: 1 / -1;
  grid-row: 1;
}

.cf-antal {
  grid-column: 1;
  grid-row: 2;
}

.cf-beloeb {
  grid-column: 2;
  grid-row: 2;
}

.cf-hint {
  grid-column: 1 / -1;
  grid-row: 3;
  margin: -0.25rem 0 0;
  padding: 0 1rem;
}

.cf-dato {
  grid-column: 1;
  grid-row: 4;
}

.cf-omk {
  grid-column: 2;
  grid-row: 4;
}

.cf-beskrivelse {
  grid-column: 1 / -1;
  grid-row: 5;
}

.d-input-group .d-input {
  height: 34px;
}

.d-input-group .d-input:disabled {
  background: #f3f4f6;
  color: #9ca3af;
  cursor: not-allowed;
}

.d-input-group {
  margin-bottom: 0.5rem;
}

.text-explainer--danger {
  color: var(--danger);
  margin-top: 0.5rem;
}
</style>



// WEBPACK FOOTER //
// SdCreateEngangsydelse.vue?654addcc